import Flex from '@Components/Bases/Flex';
import { FormInput } from '@Components/Form';
import FormCheckbox from '@Components/Form/FormCheckBox';
import { disabledFormInputStyles } from '@Components/Form/styles';
import { useGetInfoCategoryMasterData } from '@Domains/MasterData';
import { Alert, Box, InputLabel, styled, Typography } from '@mui/material';
import { useState } from 'react';
import { useGreenhouseForm } from '../EsgGreenhouseFormProvider';
import { checkAtLeastOnePrinciple, checkEmptyPrinciple } from './utils';
import { otherFieldsInteracted } from './validators';

const LabelStyles = styled(InputLabel)({
  width: '100%',
  minWidth: 200,
  maxWidth: 350,
  textAlign: 'right',
  whiteSpace: 'inherit',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-end',
  '&& .MuiInputLabel-asterisk': {
    whiteSpace: 'nowrap',
  },
});

export enum GREENHOUSE_STANDARD_LIST_ID {
  OTHER = 230,
}

const GreenhouseStandardList = () => {
  const {
    setValue,
    getValues,
    watch,
    formState: { errors },
    trigger,
  } = useGreenhouseForm();
  const [{ data: standards = [], loading, error }] =
    useGetInfoCategoryMasterData('GH_GAS_COMPLIANCE');

  const greenhouseStandard = watch('greenhouseStandard');
  const principles = getValues(`greenhouseStandard.principles`) ?? {};

  const [isOtherChecked, setIsOtherChecked] = useState(
    principles[`principle${GREENHOUSE_STANDARD_LIST_ID.OTHER}`]?.checked ??
      false
  );

  const { isCompletedTh = false, isCompletedEn = false } =
    greenhouseStandard?.completedSection ?? {};

  const handleStandardChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    standardId: number
  ) => {
    const isChecked = event.target.checked;

    const principleValues = Object.values(principles);
    const isNoPrinciple = checkEmptyPrinciple(principleValues);
    const isAtLeastOnePrinciple = checkAtLeastOnePrinciple(principleValues);
    const validatePrinciple = errors.greenhouseStandard?.principles;

    if (isNoPrinciple || isAtLeastOnePrinciple || validatePrinciple) {
      trigger(`greenhouseStandard.principles`);
    }

    setValue(
      `greenhouseStandard.principles.principle${standardId}.infoCategoryMasterDataId`,
      standardId
    );

    if (standardId === GREENHOUSE_STANDARD_LIST_ID.OTHER) {
      setIsOtherChecked(isChecked);

      if (!isChecked) {
        otherFieldsInteracted.nameTh = false;
        otherFieldsInteracted.nameEn = false;

        setValue(
          `greenhouseStandard.principles.principle${standardId}.nameTh`,
          ''
        );
        setValue(
          `greenhouseStandard.principles.principle${standardId}.nameEn`,
          ''
        );
      }
    }
  };

  const handleOtherFieldChange = (
    field: 'nameTh' | 'nameEn',
    value: string
  ) => {
    otherFieldsInteracted[field] = true;

    setValue(
      `greenhouseStandard.principles.principle${GREENHOUSE_STANDARD_LIST_ID.OTHER}.${field}`,
      value
    );

    if (isOtherChecked) {
      trigger(
        `greenhouseStandard.principles.principle${GREENHOUSE_STANDARD_LIST_ID.OTHER}`
      );
    }
  };

  if (loading) {
    return (
      <Box mt={2}>
        <Typography>Loading ...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={2}>
        <Alert severity={'error'}>
          ไม่สามารถโหลดข้อมูลหลักการและมาตรฐานด้านการจัดการก๊าซเรือนกระจกได้
        </Alert>
      </Box>
    );
  }

  return (
    <Flex mt={2} gap={3}>
      <Flex flexDirection={'column'} alignItems={'flex-end'}>
        <LabelStyles required>
          หลักการและมาตรฐานด้านการจัดการก๊าซเรือนกระจกหรือ
          การเปลี่ยนแปลงสภาพภูมิอากาศ
        </LabelStyles>
        {errors?.greenhouseStandard?.principles && (
          <Typography color={'error'} variant={'caption'} mt={1}>
            {errors.greenhouseStandard.principles.message as string}
          </Typography>
        )}
      </Flex>
      <Box>
        {standards.map((standard) => {
          return (
            <FormCheckbox
              key={standard.id}
              name={`greenhouseStandard.principles.principle${standard.id}.checked`}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleStandardChange(event, standard.id)
              }
              label={standard.nameTh}
              inputProps={{ disabled: isCompletedTh || isCompletedEn }}
              sx={{
                '& .MuiCheckbox-root': { paddingTop: 0 },
                '& .MuiFormControlLabel-root': { alignItems: 'start' },
              }}
            />
          );
        })}
        <Flex
          mt={1}
          p={1}
          flexDirection={'column'}
          bgcolor={'grey.100'}
          border={'1px solid hsl(220, 2%, 90%)'}
          borderRadius={2}
          maxWidth={625}
        >
          <FormInput
            name={`greenhouseStandard.principles.principle${GREENHOUSE_STANDARD_LIST_ID.OTHER}.nameTh`}
            placeholder={'กรุณาระบุอื่นๆ (ไทย)'}
            onChange={(e) => handleOtherFieldChange('nameTh', e.target.value)}
            sx={
              !isOtherChecked || isCompletedTh
                ? disabledFormInputStyles
                : undefined
            }
          />
          <FormInput
            name={`greenhouseStandard.principles.principle${GREENHOUSE_STANDARD_LIST_ID.OTHER}.nameEn`}
            onChange={(e) => handleOtherFieldChange('nameEn', e.target.value)}
            placeholder={'กรุณาระบุอื่นๆ (อังกฤษ)'}
            sx={
              !isOtherChecked || isCompletedEn
                ? { ...disabledFormInputStyles, mt: 1 }
                : { mt: 1 }
            }
          />
        </Flex>
      </Box>
    </Flex>
  );
};

export default GreenhouseStandardList;
